import React, { useContext, useEffect, useState } from "react";
import { ButtonDS, GlobalIcon } from "get-life-storybook-ts";
import { I18nContext } from "../../../../../../context/i18n.context";
import { GetLifeTrackerContext } from "../../../../../../context/GetLifeTracker.context";
import HttpProductInterestRepository from "../../../../../../api/request/ProductInterest/ProductInterest.service";
import { cardStyle, sectionTitleStyle, sectionTitleText } from "../cardStyles";

interface OtherInsuranceCardProps {
  leadId?: string;
  token?: string;
  // Solo ES y marca blanca ES tienen la lista de espera; el resto ve la tarjeta inerte.
  waitlistEnabled: boolean;
}

type WaitlistStateT = "loading" | "idle" | "submitting" | "registered" | "error";

const OtherInsuranceCard = ({ leadId, token, waitlistEnabled }: OtherInsuranceCardProps) => {
  const {
    state: { translate },
  } = useContext(I18nContext);
  const { handleTrackerQuestion } = useContext(GetLifeTrackerContext);

  const canRegister = waitlistEnabled && !!leadId && !!token;
  const [waitlist, setWaitlist] = useState<WaitlistStateT>("loading");

  useEffect(() => {
    if (!canRegister) return;
    let cancelled = false;
    new HttpProductInterestRepository(leadId, token)
      .get("health")
      .then((res) => {
        if (!cancelled) setWaitlist(res.registered ? "registered" : "idle");
      })
      .catch(() => {
        // Sin estado mostramos el botón: el registro es idempotente en backend.
        if (!cancelled) setWaitlist("idle");
      });
    return () => {
      cancelled = true;
    };
  }, [canRegister, leadId, token]);

  const handleNotifyMe = () => {
    setWaitlist("submitting");
    handleTrackerQuestion({
      type: "GA",
      data: { event: "healthWaitlistClick", leadId, product: "health" },
    });
    new HttpProductInterestRepository(leadId, token)
      .register("health")
      .then(() => setWaitlist("registered"))
      .catch(() => setWaitlist("error"));
  };

  const muted = !canRegister;

  return (
    <div
      style={{
        ...cardStyle,
        position: "relative",
        ...(muted ? { opacity: 0.5, pointerEvents: "none", userSelect: "none" } : {}),
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 16,
          right: 16,
          backgroundColor: "#fef3c7",
          color: "#92400e",
          fontSize: 12,
          fontWeight: 600,
          padding: "3px 12px",
          borderRadius: 20,
        }}
      >
        {translate("newDashboard.redesign.comingSoon")}
      </span>
      <div style={sectionTitleStyle}>
        <GlobalIcon iconName="Heart2Icon" size="XS" color={muted ? "#9ca3af" : "#4f46e5"} />
        <h2 style={muted ? { ...sectionTitleText, color: "#9ca3af" } : sectionTitleText}>
          {translate("newDashboard.crossSell.health.title")}
        </h2>
      </div>
      <p style={{ fontSize: 14, color: muted ? "#9ca3af" : "#4b5563", margin: "8px 0 0" }}>
        {translate("newDashboard.crossSell.health.desc")}
      </p>
      {canRegister && waitlist === "registered" && (
        <p
          role="status"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 14,
            fontWeight: 600,
            color: "#047857",
            margin: "20px 0 0",
          }}
        >
          <GlobalIcon iconName="ShieldCheckIcon" size="XS" color="#047857" />
          {translate("newDashboard.crossSell.health.notifyDone")}
        </p>
      )}
      {canRegister && waitlist !== "registered" && (
        <div style={{ marginTop: 20 }}>
          <ButtonDS
            label={translate("newDashboard.crossSell.health.notifyCta")}
            buttonType="secondary"
            size="40"
            disabled={waitlist === "loading" || waitlist === "submitting"}
            onClick={handleNotifyMe}
          />
          {waitlist === "error" && (
            <p role="alert" style={{ fontSize: 13, color: "#b91c1c", margin: "8px 0 0" }}>
              {translate("newDashboard.crossSell.health.notifyError")}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default OtherInsuranceCard;
